import Polygon from 'ol/geom/Polygon';
import Feature from 'ol/Feature';

/**
 * 폴리곤 생성 관련 기능을 담당하는 클래스
 */
export class PolygonCreator {
  /**
   * 좌표 배열의 유효성 검증
   * @param {Array} coordinates - 좌표 배열
   * @returns {boolean} 유효성 여부
   */
  validatePolygonCoordinates(coordinates) {
    if (!coordinates || !Array.isArray(coordinates) || coordinates.length < 3) {
      console.warn('PolygonCreator: 유효하지 않은 좌표 배열');
      return false;
    }

    // 각 좌표가 유효한지 확인
    const isValid = coordinates.every(coord =>
      Array.isArray(coord) && coord.length >= 2 &&
      typeof coord[0] === 'number' && typeof coord[1] === 'number'
    );

    if (!isValid) {
      console.warn('PolygonCreator: 좌표 형식이 올바르지 않음');
      return false;
    }

    return true;
  }

  /**
   * 닫힌 폴리곤 좌표 생성 (첫 번째와 마지막 좌표가 같도록)
   * @param {Array} coordinates - 원본 좌표 배열
   * @returns {Array} 닫힌 좌표 배열
   */
  createClosedPolygonCoordinates(coordinates) {
    if (!this.validatePolygonCoordinates(coordinates)) {
      return null;
    }

    const closedCoordinates = [...coordinates];

    // 첫 번째와 마지막 좌표가 다르면 첫 번째 좌표를 마지막에 추가
    const firstCoord = closedCoordinates[0];
    const lastCoord = closedCoordinates[closedCoordinates.length - 1];

    if (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
      closedCoordinates.push([firstCoord[0], firstCoord[1]]);
    }

    return closedCoordinates;
  }

  /**
   * 폴리곤 지오메트리 생성
   * @param {Array} coordinates - 좌표 배열
   * @returns {Polygon|null} 생성된 폴리곤 지오메트리
   */
  createPolygonGeometry(coordinates) {
    const closedCoordinates = this.createClosedPolygonCoordinates(coordinates);

    if (!closedCoordinates) {
      return null;
    }

    try {
      const polygon = new Polygon([closedCoordinates]);
      console.log('PolygonCreator: 폴리곤 지오메트리 생성 완료');
      return polygon;
    } catch (error) {
      console.error('PolygonCreator: 폴리곤 지오메트리 생성 실패:', error);
      return null;
    }
  }

  /**
   * 폴리곤 피처 생성
   * @param {Array} coordinates - 좌표 배열
   * @param {string} imageId - 이미지 ID
   * @param {Object} additionalProperties - 추가 속성
   * @returns {Feature|null} 생성된 폴리곤 피처
   */
  createPolygonFeature(coordinates, imageId, additionalProperties = {}) {
    const polygon = this.createPolygonGeometry(coordinates);

    if (!polygon) {
      return null;
    }

    try {
      const feature = new Feature({
        geometry: polygon,
        imageId: imageId,
        ...additionalProperties
      });

      // 피처 ID 설정
      feature.setId(`polygon-${imageId}`);

      console.log(`PolygonCreator: 폴리곤 피처 생성 완료 - ID: ${imageId}`);
      return feature;
    } catch (error) {
      console.error('PolygonCreator: 폴리곤 피처 생성 실패:', error);
      return null;
    }
  }

  /**
   * 폴리곤 좌표에서 폴리곤 피처 생성 (간단한 버전)
   * @param {Array} coordinates - 좌표 배열
   * @param {string} imageId - 이미지 ID
   * @returns {Feature|null} 생성된 폴리곤 피처
   */
  createSimplePolygonFeature(coordinates, imageId) {
    return this.createPolygonFeature(coordinates, imageId);
  }

  /**
   * 폴리곤 좌표 업데이트
   * @param {Feature} feature - 폴리곤 피처
   * @param {Array} newCoordinates - 새로운 좌표 배열
   * @returns {boolean} 업데이트 성공 여부
   */
  updatePolygonFeatureCoordinates(feature, newCoordinates) {
    if (!feature || !this.validatePolygonCoordinates(newCoordinates)) {
      return false;
    }

    const newPolygon = this.createPolygonGeometry(newCoordinates);

    if (!newPolygon) {
      return false;
    }

    try {
      feature.setGeometry(newPolygon);
      console.log('PolygonCreator: 폴리곤 좌표 업데이트 완료');
      return true;
    } catch (error) {
      console.error('PolygonCreator: 폴리곤 좌표 업데이트 실패:', error);
      return false;
    }
  }
}
